import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Library, Trash2, Sparkles, Layers, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SnapshotsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const refresh = () => {
    setLoading(true);
    api.get("/snapshots").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Delete snapshot "${s.name}"?`)) return;
    try {
      await api.delete(`/snapshots/${s.id}`);
      setItems((prev) => prev.filter((x) => x.id !== s.id));
      toast.success("Snapshot deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow text-gold flex items-center gap-2"><Library className="w-3.5 h-3.5" /> Library</div>
            <h1 className="font-display text-5xl text-navy mt-3">Industry Snapshots</h1>
            <div className="w-12 h-[2px] bg-gold mt-4" />
            <p className="text-navy/60 mt-4 max-w-2xl">
              Reusable Goldline build templates. When you analyze a new client, snapshots
              matching their industry are auto-fed into the prompt so the LLM proposes the
              same proven stack — your moat for repeat-industry sales.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-navy/50">Loading...</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-navy/10 p-16 text-center">
            <Layers className="w-10 h-10 text-gold mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-2xl text-navy">No snapshots yet</h3>
            <p className="text-navy/60 mt-2 max-w-md mx-auto">
              When you generate an assessment you're proud of, click <strong>"Save as Snapshot"</strong> on the assessment page to add it here.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((s) => (
              <div key={s.id} data-testid={`snapshot-card-${s.id}`}
                className="bg-white border border-navy/10 hover:border-gold p-5 flex flex-col group transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="eyebrow text-gold">{s.industry}</div>
                    <h3 className="font-display text-xl text-navy mt-1.5 leading-tight">{s.name}</h3>
                  </div>
                  <button
                    onClick={() => remove(s)}
                    data-testid={`delete-snapshot-${s.id}`}
                    className="text-navy/30 hover:text-red-600 transition opacity-0 group-hover:opacity-100"
                    title="Delete snapshot">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {s.summary && (
                  <p className="text-sm text-navy/60 mt-3 line-clamp-3">{s.summary}</p>
                )}
                <div className="mt-4 flex items-center gap-3 text-xs text-navy/50">
                  <span className="bg-ivory/80 px-2 py-1 border border-navy/10">{(s.recommended_solutions || []).length} tools</span>
                  <span className="bg-ivory/80 px-2 py-1 border border-navy/10">{(s.phase_two_projects || []).length} phase-2</span>
                </div>
                <div className="mt-4 pt-4 border-t border-navy/5 flex items-center justify-between text-[11px] text-navy/40">
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-gold" /> Used {s.use_count || 0}×
                  </span>
                  <span>{new Date(s.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
